"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

export default function CustomCursor() {
  const pathname = usePathname();

  // Don't render custom cursor on admin dashboard
  if (pathname?.startsWith('/admin')) {
    return null;
  }

  useEffect(() => {
    // Abort if on admin pages
    if (pathname?.startsWith('/admin')) return;

    // Hide custom cursor on touch devices / mobile — touch devices have native cursors
    const isTouchDevice = typeof window !== 'undefined' && (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    );
    if (isTouchDevice) {
      // Ensure default cursor is used and do not attach custom elements
      document.body.style.cursor = 'auto';
      return;
    }

    let cursorOuter, cursorInner;
    let mouseX = 0,
      mouseY = 0;
    let outerX = 0,
      outerY = 0;
    let innerX = 0,
      innerY = 0;
    let rafId;
    let isHovering = false;
    let isOnYellowButton = false;
    let overlayCheckInterval;

    function createCursors() {
      // Create outer cursor (ring)
      cursorOuter = document.createElement("div");
      cursorOuter.className = "custom-cursor-outer";
      cursorOuter.style.cssText = `
        position: fixed;
        width: 30px;
        height: 30px;
        border: 2px solid #dfa428;
        border-radius: 50%;
        pointer-events: none;
        z-index: 2147483646;
        left: 0;
        top: 0;
        transform: translate(-50%, -50%);
        transition: width 0.2s ease, height 0.2s ease, border-color 0.2s ease, background-color 0.2s ease, border-radius 0.2s ease;
        background: rgba(223, 164, 40, 0.1);
        backdrop-filter: blur(2px);
        box-shadow: 0 0 15px rgba(223, 164, 40, 0.4);
      `;

      // Create inner cursor (dot)
      cursorInner = document.createElement("div");
      cursorInner.className = "custom-cursor-inner";
      cursorInner.style.cssText = `
        position: fixed;
        width: 8px;
        height: 8px;
        background: #dfa428;
        border-radius: 50%;
        pointer-events: none;
        z-index: 2147483647;
        left: 0;
        top: 0;
        transform: translate(-50%, -50%);
        transition: width 0.15s ease, height 0.15s ease, background-color 0.15s ease, border-radius 0.15s ease;
      `;

      document.body.appendChild(cursorOuter);
      document.body.appendChild(cursorInner);
    }

    function analyzeBackgroundColor(element) {
      const style = window.getComputedStyle(element);
      const rgb = style.backgroundColor;
      const match = rgb.match(/\d+/g);
      if (!match) return { brightness: 255, hasBlue: false, hasYellow: false, isWhite: false };

      const [r, g, b] = match.map(Number);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;

      // كشف الأزرق بدقة أكبر
      const hasBlue = b > 20;

      // كشف الأصفر/الذهبي - معادلة دقيقة
      // #dfa428 = rgb(223, 164, 40)
      // نتحقق من: أحمر عالي، أخضر متوسط-عالي، أزرق منخفض
      const hasYellow = (
        // الذهبي الأساسي #dfa428
        (r >= 200 && g >= 140 && b <= 80) ||
        // أصفر عام
        (r > 180 && g > 120 && b < 120 && r > b * 2 && g > b * 1.5) ||
        // أصفر فاتح
        (r > 240 && g > 220 && b < 150)
      );

      // كشف الأبيض
      const isWhite = r > 200 && g > 200 && b > 200;

      return { brightness, hasBlue, hasYellow, isWhite };
    }

    function updateCursorColor() {
      // Skip update if we're hovering over interactive elements
      // The hover handler will manage the color
      if (isHovering) {
        return;
      }

      const elementUnderCursor = document.elementFromPoint(mouseX, mouseY);
      if (!elementUnderCursor) return;

      let element = elementUnderCursor;
      let hasYellowBackground = false;

      // Check up to 15 parent elements for yellow background
      for (let i = 0; i < 15 && element && element !== document.body; i++) {
        const style = window.getComputedStyle(element);
        const bg = style.backgroundColor;

        if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
          const analysis = analyzeBackgroundColor(element);
          if (analysis.hasYellow) {
            hasYellowBackground = true;
            break;
          }
        }
        element = element.parentElement;
      }

      // Set cursor to blue if on yellow background, gold otherwise
      if (hasYellowBackground) {
        if (cursorOuter) {
          cursorOuter.style.borderColor = "#0a367b";
          cursorOuter.style.background = "rgba(10, 54, 123, 0.15)";
          cursorOuter.style.boxShadow = "0 0 15px rgba(10, 54, 123, 0.4)";
        }
        if (cursorInner) {
          cursorInner.style.background = "#0a367b";
        }
      } else {
        if (cursorOuter) {
          cursorOuter.style.borderColor = "#dfa428";
          cursorOuter.style.background = "rgba(223, 164, 40, 0.15)";
          cursorOuter.style.boxShadow = "0 0 15px rgba(223, 164, 40, 0.4)";
        }
        if (cursorInner) {
          cursorInner.style.background = "#dfa428";
        }
      }

      // Dot-only mode: if the element or any ancestor requests dot-only (or is a video), hide outer ring
      const isDotOnlyNow = !!(elementUnderCursor.closest && (
        elementUnderCursor.closest('.cursor-dot-only') ||
        elementUnderCursor.tagName === 'VIDEO' ||
        elementUnderCursor.closest('button.cursor-dot-only') ||
        elementUnderCursor.closest('[data-hide-native-cursor]')
      ));
      // Detect if we're inside a section that requested hiding native cursor (video section)
      const inVideoSection = !!(elementUnderCursor.closest && elementUnderCursor.closest('[data-hide-native-cursor]'));

      if (isDotOnlyNow) {
        // hide outer ring
        if (cursorOuter) {
          cursorOuter.style.display = 'none';
        }
        if (cursorInner) {
          // animate via transform scale for smooth grow/shrink
          const scale = inVideoSection ? 2.6 : 1.4; // larger scale inside video section
          cursorInner.style.transition = 'transform 160ms ease, background-color 120ms ease';
          cursorInner.style.transform = `translate(-50%, -50%) scale(${scale})`;
          cursorInner.style.background = isOnYellowButton ? '#0a367b' : '#dfa428';
        }
      } else {
        if (cursorOuter) {
          cursorOuter.style.display = 'block';
        }
        if (cursorInner) {
          cursorInner.style.transition = 'transform 160ms ease, background-color 120ms ease';
          cursorInner.style.transform = 'translate(-50%, -50%) scale(1)';
          // ensure default size/color preserved
          cursorInner.style.background = isOnYellowButton ? '#0a367b' : '#dfa428';
        }
      }
    }

    function onMouseMove(e) {
      mouseX = e.clientX;
      mouseY = e.clientY;
    }

    let updateColorCounter = 0;

    function animateCursor() {
      outerX += (mouseX - outerX) * 0.15;
      outerY += (mouseY - outerY) * 0.15;
      innerX += (mouseX - innerX) * 0.3;
      innerY += (mouseY - innerY) * 0.3;

      if (cursorOuter) {
        cursorOuter.style.left = outerX + "px";
        cursorOuter.style.top = outerY + "px";
      }

      if (cursorInner) {
        cursorInner.style.left = innerX + "px";
        cursorInner.style.top = innerY + "px";
      }

      // Update color every frame when not hovering, less frequently when hovering
      if (isHovering) {
        updateColorCounter++;
        if (updateColorCounter >= 3) {
          updateColorCounter = 0;
          updateCursorColor();
        }
      } else {
        updateCursorColor();
      }

      rafId = requestAnimationFrame(animateCursor);
    }

    function addHoverEffects() {
      const interactiveSelector =
        'a, button, [role="button"], input[type="submit"], input[type="button"], select, label, .cursor-pointer, [onclick], [data-hide-native-cursor], video, .cursor-dot-only';

      document.addEventListener(
        "mouseover",
        (e) => {
          if (e.target.closest(interactiveSelector)) {
            isHovering = true;

            // Find the actual button/link element, not inner content
            const element = e.target.closest(interactiveSelector);

            // Check if it's a yellow button by class or by background color
            let useBlueOnHover = false;

            // PRIORITY 1: Check for blue-cursor class (elements that should ALWAYS keep gold cursor)
            // This takes absolute priority over any background color detection
            const hasBlueClass = element.classList?.contains('blue-cursor') || element.closest('.blue-cursor');

            if (hasBlueClass) {
              // Force gold cursor, ignore all color detection
              useBlueOnHover = false;
            }
            // PRIORITY 2: Check for yellow-button class
            else if (element.classList?.contains('yellow-button') || element.closest('.yellow-button')) {
              useBlueOnHover = true;
            } else {
              // PRIORITY 3: Fallback - check background color only if no classes found
              let checkElement = element;

              // Check the element itself first
              const elementStyle = window.getComputedStyle(element);
              const elementBg = elementStyle.backgroundColor;

              if (elementBg && elementBg !== 'rgba(0, 0, 0, 0)' && elementBg !== 'transparent') {
                const analysis = analyzeBackgroundColor(element);
                if (analysis.hasYellow) {
                  useBlueOnHover = true;
                }
              }

              // If still not found, check parent elements only
              if (!useBlueOnHover) {
                for (let i = 0; i < 3 && checkElement; i++) {
                  const style = window.getComputedStyle(checkElement);
                  const bg = style.backgroundColor;

                  if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
                    const analysis = analyzeBackgroundColor(checkElement);
                    if (analysis.hasYellow) {
                      useBlueOnHover = true;
                      break;
                    }
                    break;
                  }
                  checkElement = checkElement.parentElement;
                }
              }
            }

            // Set isOnYellowButton based on hasBlueClass priority
            if (hasBlueClass) {
              isOnYellowButton = false; // Always false for blue-cursor elements
            } else {
              isOnYellowButton = useBlueOnHover;
            }

            // Hide native cursor on the hovered interactive element so only custom cursor is visible
            try {
              if (element && typeof element.style !== 'undefined') {
                // store previous cursor so we can restore it on mouseout
                element.dataset.__prevCursor = element.style.cursor || '';
                element.style.cursor = 'none';
              }
            } catch (err) {
              // ignore
            }

            // Apply hover effect with appropriate color
            if (hasBlueClass) {
              // Force gold cursor for blue-cursor class elements
              if (cursorOuter) {
                cursorOuter.style.width = "50px";
                cursorOuter.style.height = "50px";
                cursorOuter.style.borderColor = "#dfa428";
                cursorOuter.style.background = "rgba(223, 164, 40, 0.2)";
                cursorOuter.style.boxShadow = "0 0 20px rgba(223, 164, 40, 0.4)";
              }
              if (cursorInner) {
                cursorInner.style.width = "4px";
                cursorInner.style.height = "4px";
                cursorInner.style.background = "#dfa428";
              }
            } else if (useBlueOnHover) {
              // Blue cursor for yellow buttons
              if (cursorOuter) {
                cursorOuter.style.width = "50px";
                cursorOuter.style.height = "50px";
                cursorOuter.style.borderColor = "#0a367b";
                cursorOuter.style.background = "rgba(10, 54, 123, 0.2)";
                cursorOuter.style.boxShadow = "0 0 20px rgba(10, 54, 123, 0.4)";
              }
              if (cursorInner) {
                cursorInner.style.width = "4px";
                cursorInner.style.height = "4px";
                cursorInner.style.background = "#0a367b";
              }
            } else {
              // Gold cursor for other elements
              if (cursorOuter) {
                cursorOuter.style.width = "50px";
                cursorOuter.style.height = "50px";
                cursorOuter.style.borderColor = "#dfa428";
                cursorOuter.style.background = "rgba(223, 164, 40, 0.2)";
                cursorOuter.style.boxShadow = "0 0 20px rgba(223, 164, 40, 0.4)";
              }
              if (cursorInner) {
                cursorInner.style.width = "4px";
                cursorInner.style.height = "4px";
                cursorInner.style.background = "#dfa428";
              }
            }
          }
        },
        true
      );

      document.addEventListener(
        "mouseout",
        (e) => {
          if (e.target.closest(interactiveSelector)) {
            isHovering = false;
            isOnYellowButton = false;
            if (cursorOuter) {
              cursorOuter.style.width = "30px";
              cursorOuter.style.height = "30px";
            }
            if (cursorInner) {
              cursorInner.style.width = "8px";
              cursorInner.style.height = "8px";
            }
            // Restore native cursor on the element we previously changed
            try {
              const el = e.target.closest(interactiveSelector);
              if (el && typeof el.style !== 'undefined' && el.dataset.__prevCursor !== undefined) {
                el.style.cursor = el.dataset.__prevCursor || '';
                delete el.dataset.__prevCursor;
              }
            } catch (err) {
              // ignore
            }
            // Force immediate color update when leaving interactive element
            updateCursorColor();
          }
        },
        true
      );

      const textInputs =
        'input[type="text"], input[type="email"], input[type="password"], input[type="search"], input[type="number"], input[type="tel"], input[type="url"], textarea';

      document.addEventListener(
        "mouseover",
        (e) => {
          if (e.target.matches(textInputs)) {
            if (cursorOuter) {
              cursorOuter.style.width = "3px";
              cursorOuter.style.height = "24px";
              cursorOuter.style.borderRadius = "2px";
            }
            if (cursorInner) {
              cursorInner.style.width = "2px";
              cursorInner.style.height = "20px";
              cursorInner.style.borderRadius = "1px";
            }
          }
        },
        true
      );

      document.addEventListener(
        "mouseout",
        (e) => {
          if (e.target.matches(textInputs)) {
            if (cursorOuter) {
              cursorOuter.style.width = "30px";
              cursorOuter.style.height = "30px";
              cursorOuter.style.borderRadius = "50%";
            }
            if (cursorInner) {
              cursorInner.style.width = "8px";
              cursorInner.style.height = "8px";
              cursorInner.style.borderRadius = "50%";
            }
          }
        },
        true
      );

      document.addEventListener("mousedown", () => {
        if (cursorOuter) {
          cursorOuter.style.transform = "translate(-50%, -50%) scale(0.8)";
        }
        if (cursorInner) {
          cursorInner.style.transform = "translate(-50%, -50%) scale(1.5)";
        }
      });

      document.addEventListener("mouseup", () => {
        if (cursorOuter) {
          cursorOuter.style.transform = "translate(-50%, -50%) scale(1)";
        }
        if (cursorInner) {
          cursorInner.style.transform = "translate(-50%, -50%) scale(1)";
        }
      });
    }

    createCursors();

    // Show system cursor in fullscreen video
    function handleFullscreenChange() {
      const isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
      // Detect fullscreen video element
      let fsVideo = null;
      if (document.fullscreenElement && document.fullscreenElement.tagName === 'VIDEO') fsVideo = document.fullscreenElement;
      if (document.webkitFullscreenElement && document.webkitFullscreenElement.tagName === 'VIDEO') fsVideo = document.webkitFullscreenElement;
      if (document.mozFullScreenElement && document.mozFullScreenElement.tagName === 'VIDEO') fsVideo = document.mozFullScreenElement;
      if (document.msFullscreenElement && document.msFullscreenElement.tagName === 'VIDEO') fsVideo = document.msFullscreenElement;

      if (isFullscreen) {
        document.body.style.cursor = 'auto';
        if (cursorOuter) cursorOuter.style.display = 'none';
        if (cursorInner) cursorInner.style.display = 'none';
        if (fsVideo) {
          fsVideo.style.cursor = 'auto';
          // Also set cursor:auto on parent element (fullscreen container)
          let parent = fsVideo.parentElement;
          let tries = 0;
          while (parent && tries < 6) {
            parent.style.cursor = 'auto';
            // Remove any [data-hide-native-cursor] attribute to allow system cursor
            if (parent.hasAttribute && parent.hasAttribute('data-hide-native-cursor')) {
              parent.removeAttribute('data-hide-native-cursor');
            }
            // If parent is fullscreen element, break
            if (parent === document.fullscreenElement || parent === document.webkitFullscreenElement) break;
            parent = parent.parentElement;
            tries++;
          }
        }
      } else {
        document.body.style.cursor = 'none';
        if (cursorOuter) cursorOuter.style.display = 'block';
        if (cursorInner) cursorInner.style.display = 'block';
        // Restore all video cursors and parent containers
        document.querySelectorAll('video').forEach(v => { v.style.cursor = 'none';
          let parent = v.parentElement;
          let tries = 0;
          while (parent && tries < 6) {
            parent.style.cursor = '';
            // Restore [data-hide-native-cursor] if it was present before
            if (parent.classList && parent.classList.contains('video-section')) {
              parent.setAttribute('data-hide-native-cursor', '');
            }
            parent = parent.parentElement;
            tries++;
          }
        });
      }
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    // Ensure immediate switch when entering video sections
    let sectionEls = [];
    const handleSectionEnter = (e) => {
      isHovering = true;
      // hide outer ring and scale inner immediately
      if (cursorOuter) cursorOuter.style.display = 'none';
      if (cursorInner) {
        cursorInner.style.transition = 'transform 120ms ease';
        cursorInner.style.transform = 'translate(-50%, -50%) scale(2.6)';
      }
    };
    const handleSectionLeave = (e) => {
      isHovering = false;
      if (cursorOuter) cursorOuter.style.display = 'block';
      if (cursorInner) {
        cursorInner.style.transition = 'transform 120ms ease';
        cursorInner.style.transform = 'translate(-50%, -50%) scale(1)';
      }
    };
    try {
      sectionEls = Array.from(document.querySelectorAll('[data-hide-native-cursor]'));
      sectionEls.forEach(el => {
        el.addEventListener('mouseenter', handleSectionEnter);
        el.addEventListener('mouseleave', handleSectionLeave);
      });
    } catch (err) {
      // ignore
    }
    document.addEventListener("mousemove", onMouseMove);
    addHoverEffects();
    animateCursor();

    // Simply ensure cursors are always visible
    // Don't check for overlays - keep cursor visible always
    if (cursorOuter && cursorInner) {
      cursorOuter.style.display = 'block';
      cursorInner.style.display = 'block';
      document.body.style.cursor = 'none';
    }

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
      if (overlayCheckInterval) clearInterval(overlayCheckInterval);
      if (cursorOuter) cursorOuter.remove();
      if (cursorInner) cursorInner.remove();
      // cleanup section listeners
      try {
        sectionEls.forEach(el => {
          el.removeEventListener('mouseenter', handleSectionEnter);
          el.removeEventListener('mouseleave', handleSectionLeave);
        });
      } catch (err) {
        // ignore
      }
      document.body.style.cursor = 'auto'; // Restore default cursor
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  return null;
}
